import { IonModal } from '@ionic/react'
import { Button, GlobalStyles, Stack, Typography } from '@mui/material'
import { useCallback } from 'react'
import { PointDisplay } from 'utoniq-ui'

import {
  GachaTicketConnectionFragment,
  Maybe,
  PrepaidPointBalance,
  PurchasableGachaTicket,
} from '@/clients/utoniq-core/schema'
import { BrowserLink } from '@/components/lib/BrowserLink'
import { Display, HeaderDisplay } from '@/components/organisms'
import { styles } from '@/components/styles'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

type Props = {
  gachaId: string
  artistId: string
  ticket: number | string
  purchasableGachaTickets: Maybe<Omit<PurchasableGachaTicket, 'id'>>[]
  prepaidPointBalance: Maybe<
    Pick<
      PrepaidPointBalance,
      'prepaidPointId' | 'balance' | 'formattedPrepaidPointBarance'
    >
  >
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  open: boolean
  onClose: () => void
  setOpenTicketPurchaseConfirmModal: (open: boolean) => void
  setPurchasableGachaTicket: (
    ticket: Maybe<Omit<PurchasableGachaTicket, 'id'>>
  ) => void
  setOpenPointCharge: () => void
}

/**
 * @description
 * チケット購入用アクションシート
 */
export const PurchaseGachaTicketDrawer: Cell<Props> = ({
  ticket,
  onClose,
  gachaId,
  artistId,
  prepaidPointBalance,
  purchasableGachaTickets,
  gachaTicket,
  open,
  setOpenTicketPurchaseConfirmModal,
  setPurchasableGachaTicket,
  setOpenPointCharge,
}) => {
  const { t } = useTranslation('gachaDetail')
  const { analytics } = useAnalytics()

  const handleClickGachaTicket = useCallback(
    async (newPurchasableGachaTicket: Omit<PurchasableGachaTicket, 'id'>) => {
      setPurchasableGachaTicket(newPurchasableGachaTicket)
      setOpenTicketPurchaseConfirmModal(true)
    },
    []
  )

  return (
    <>
      <GlobalStyles
        styles={{
          'ion-modal#purchaseGachaTicketDrawer': {
            '--height': 'auto',
          },
        }}
      />
      <IonModal
        className="parent-modal"
        isOpen={open}
        onDidDismiss={onClose}
        initialBreakpoint={1}
        breakpoints={[0, 1]}
        id="purchaseGachaTicketDrawer"
      >
        <CellFrame sx={{ bgcolor: 'grey.50', px: 5, py: 3 }}>
          <Typography variant="h6" sx={{ py: 2 }}>
            {t('buyTicket')}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row">
              <Display
                ticket={ticket ?? 0}
                src={gachaTicket?.thumbnails?.standard?.smallUri || ''}
              />
              <PointDisplay
                point={prepaidPointBalance?.balance ?? 0}
                sx={{ ml: 3 }}
              />
            </Stack>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={() => {
                analytics?.track('click_getPrepaidPoint', {
                  applicationKey: config.applicationKey,
                  name: 'gachaDetail',
                  gachaId,
                  artistId,
                  prepaidPointId: prepaidPointBalance?.prepaidPointId,
                  type: 'bottomsheet',
                })
                setOpenPointCharge()
              }}
              sx={{ height: 28 }}
            >
              {t('buyPoints')}
            </Button>
          </Stack>
          <TicketChargeButtonCell
            purchasableGachaTickets={purchasableGachaTickets}
            onPurchaseGachaTicket={handleClickGachaTicket}
            gachaTicket={gachaTicket}
          />
          <Stack sx={{ width: 'fit-content' }}>
            <BrowserLink
              variant="caption"
              href={getHelpUrl('specifiedCommercialTransactionLaw')}
              sx={{ my: 4 }}
            >
              {t('commercialTransactionLaw')}
            </BrowserLink>
          </Stack>
          <Button
            onClick={onClose}
            variant="text"
            fullWidth
            sx={{ borderRadius: 2, height: 40 }}
          >
            <Typography variant="h6" color="primary">
              {t('cancel')}
            </Typography>
          </Button>
          <Stack sx={{ pb: styles.safeAreaBottom }} />
        </CellFrame>
      </IonModal>
    </>
  )
}

const TicketChargeButtonCell: Cell<{
  gachaTicket: Maybe<GachaTicketConnectionFragment>
  purchasableGachaTickets: Maybe<Omit<PurchasableGachaTicket, 'id'>>[]
  onPurchaseGachaTicket: (
    purchasableGachaTicket: Omit<PurchasableGachaTicket, 'id'>
  ) => Promise<void>
}> = ({ purchasableGachaTickets, onPurchaseGachaTicket, gachaTicket }) => (
  <CellFrame sx={{ py: 3 }}>
    <Stack spacing={4}>
      {purchasableGachaTickets.map(
        (purchasableGachaTicket, index) =>
          purchasableGachaTicket && (
            <Button
              key={index}
              onClick={() => onPurchaseGachaTicket(purchasableGachaTicket)}
              variant="contained"
              fullWidth
              sx={{
                bgcolor: 'grey.100',
                justifyContent: 'space-between',
                borderRadius: 2,
                height: 56,
              }}
            >
              <HeaderDisplay
                ticket={purchasableGachaTicket.displayCount ?? 0}
                src={gachaTicket?.thumbnails?.standard?.smallUri || ''}
              />
              <PointDisplay point={purchasableGachaTicket.prepaidPoints ?? 0} />
            </Button>
          )
      )}
    </Stack>
  </CellFrame>
)
